import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useStaffLog } from '../utils/useStaffLog';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const STATUS_BADGE = {
  DRAFT:     { background: '#fef3c7', color: '#d97706' },
  CONFIRMED: { background: '#dcfce7', color: '#16a34a' },
  CANCELLED: { background: '#f3f4f6', color: '#9ca3af' },
};

const METHOD_LABEL = {
  cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other',
};

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_QUICK = {
  expense_category_id: '',
  expense_date:        today(),
  description:         '',
  amount:              '',
  payment_method:      'cash',
  bank_account_id:     '',
};

/* ── Quick-Create Popup ── */
function QuickExpensePopup({ categories, onClose, onCreated }) {
  const { log } = useStaffLog();
  const [form, setForm]       = useState(EMPTY_QUICK);
  const [saving, setSaving]   = useState(false);
  const [bankAccounts, setBankAccounts] = useState([]);

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
  }, []);
  const dateRef = useRef(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.expense_category_id) return toast.error('Please select a category');
    if (!form.expense_date)        return toast.error('Expense date is required');
    if (!form.description.trim())  return toast.error('Description is required');
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Amount must be greater than 0');
    if (!form.payment_method)      return toast.error('Payment method is required');

    try {
      setSaving(true);
      const payload = {
        expense_category_id: form.expense_category_id,
        expense_date:        form.expense_date,
        description:         form.description.trim(),
        payment_method:      form.payment_method,
        bank_account_id:     form.bank_account_id || null,
        taxable_amount:      Number(form.amount),
        other_charges:       0,
        round_off:           0,
        status:              'CONFIRMED',
      };
      const { data } = await API.post('/expenses', payload);
      toast.success('Expense created');
      log('created_expense', `Created expense (₹${form.amount || 0})`, 'Expense', data?.expense?.id);
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', border: '1px solid #e5e7eb',
    borderRadius: 8, fontSize: 14, outline: 'none',
    background: '#fff',
  };
  const labelStyle = {
    display: 'block', fontSize: 12, fontWeight: 600,
    color: '#6b7280', marginBottom: 5,
  };

  return (
    /* Backdrop */
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* Modal box */}
      <div
        style={{
          background: '#fff', borderRadius: 14, width: 460,
          maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          padding: 28, position: 'relative',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>New Expense</h3>
          <button
            onClick={onClose}
            style={{
              background: '#f3f4f6', border: 'none', borderRadius: 8,
              width: 32, height: 32, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', color: '#374151',
            }}
          >
            <FiX size={16} />
          </button>
        </div>

        {/* Fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Category */}
          <div>
            <label style={labelStyle}>Category *</label>
            <select
              style={inputStyle}
              value={form.expense_category_id}
              onChange={e => set('expense_category_id', e.target.value)}
            >
              <option value="">— Select category —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label style={labelStyle}>Date *</label>
            <input
              ref={dateRef}
              type="date"
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={form.expense_date}
              onChange={e => set('expense_date', e.target.value)}
              onClick={() => dateRef.current?.showPicker?.()}
            />
          </div>

          {/* Description */}
          <div>
            <label style={labelStyle}>Description *</label>
            <input
              style={inputStyle}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Brief description of the expense"
            />
          </div>

          {/* Amount */}
          <div>
            <label style={labelStyle}>Amount (₹) *</label>
            <input
              type="number" min="0" step="0.01"
              style={inputStyle}
              value={form.amount}
              onChange={e => set('amount', e.target.value)}
              placeholder="0.00"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label style={labelStyle}>Payment Method *</label>
            <select
              style={inputStyle}
              value={form.payment_method}
              onChange={e => { set('payment_method', e.target.value); set('bank_account_id', ''); }}
            >
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Paid From Account */}
          <div>
            <label style={labelStyle}>Paid From Account</label>
            <select
              style={inputStyle}
              value={form.bank_account_id}
              onChange={e => set('bank_account_id', e.target.value)}
            >
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter(a => {
                  if (form.payment_method === 'cash')         return a.account_type === 'cash';
                  if (form.payment_method === 'upi')          return a.account_type === 'upi';
                  if (form.payment_method === 'bank_transfer' || form.payment_method === 'cheque') return a.account_type === 'bank';
                  return true;
                })
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              padding: '10px 26px', background: 'var(--primary)', color: '#fff',
              border: 'none', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Create Expense'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main Page ── */
export default function AdminExpenses() {
  const navigate = useNavigate();

  const [expenses,      setExpenses]      = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [count,         setCount]         = useState(0);
  const [pages,         setPages]         = useState(1);
  const [page,          setPage]          = useState(1);
  const [showPopup,     setShowPopup]     = useState(false);

  const [q,            setQ]            = useState('');
  const [catFilter,    setCatFilter]    = useState('');
  const [pmFilter,     setPmFilter]     = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate,     setFromDate]     = useState('');
  const [toDate,       setToDate]       = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await API.get('/expenses/categories', { params: { status: 'active' } });
      setCategories(data.categories || []);
    } catch {}
  }, []);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = { q, page, limit: 25 };
      if (catFilter)    params.category_id   = catFilter;
      if (pmFilter)     params.payment_method = pmFilter;
      if (statusFilter) params.status         = statusFilter;
      if (fromDate)     params.from_date      = fromDate;
      if (toDate)       params.to_date        = toDate;
      const { data } = await API.get('/expenses', { params });
      setExpenses(data.expenses || []);
      setPages(data.pages || 1);
      setCount(data.count || 0);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [q, catFilter, pmFilter, statusFilter, fromDate, toDate, page]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { setPage(1); }, [q, catFilter, pmFilter, statusFilter, fromDate, toDate]);
  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleCreated = () => {
    setShowPopup(false);
    fetchExpenses();
  };

  const filterStyle = {
    padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
    fontSize: 13, background: '#fff', color: '#374151', outline: 'none',
  };
  const thStyle = { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '13px 14px', fontSize: 14, borderBottom: '1px solid #f9fafb', verticalAlign: 'middle' };

  return (
    <div style={{ padding: 24 }}>

      {/* Popup */}
      {showPopup && (
        <QuickExpensePopup
          categories={categories}
          onClose={() => setShowPopup(false)}
          onCreated={handleCreated}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>Expenses</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {count} expense{count !== 1 ? 's' : ''} found
          </p>
        </div>
        <button
          onClick={() => setShowPopup(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}
        >
          <FiPlus size={15} /> New Expense
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
        <div style={{ position: 'relative', minWidth: 200 }}>
          <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            style={{ ...filterStyle, paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
            placeholder="Search expenses…"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>

        <select style={filterStyle} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select style={filterStyle} value={pmFilter} onChange={e => setPmFilter(e.target.value)}>
          <option value="">All Payment Methods</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>

        <select style={filterStyle} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="CONFIRMED">Confirmed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <input type="date" style={filterStyle} value={fromDate} onChange={e => setFromDate(e.target.value)} title="From date" />
        <input type="date" style={filterStyle} value={toDate}   onChange={e => setToDate(e.target.value)}   title="To date" />
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : expenses.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
            No expenses found. Adjust your filters or create a new expense.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Expense No.</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Description</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={thStyle}>Payment Method</th>
                <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map(exp => (
                <tr
                  key={exp.id}
                  onClick={() => navigate(`/admin/expenses/${exp.id}`)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <td style={{ ...tdStyle, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>
                    {exp.expense_number}
                  </td>
                  <td style={tdStyle}>{fmt(exp.expense_date)}</td>
                  <td style={tdStyle}>{exp.category?.name || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {exp.description}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                    {money(exp.grand_total)}
                  </td>
                  <td style={tdStyle}>{METHOD_LABEL[exp.payment_method] || exp.payment_method}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 600, ...STATUS_BADGE[exp.status] }}>
                      {exp.status.charAt(0) + exp.status.slice(1).toLowerCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, padding: '14px 0', borderTop: '1px solid #f3f4f6' }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 10px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}
            >
              <FiChevronLeft size={15} />
            </button>
            <span style={{ fontSize: 13, color: '#6b7280' }}>Page {page} of {pages}</span>
            <button
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
              style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '5px 10px', cursor: page === pages ? 'not-allowed' : 'pointer', opacity: page === pages ? 0.4 : 1 }}
            >
              <FiChevronRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}